import { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import {
  FaEnvelope,
  FaFacebook,
  FaInstagram,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaPhone,
  FaTiktok,
  FaWhatsapp,
  FaYoutube,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { Add_Client_Action } from "../Redux/Actions/ClientAction";
import Galaxy from "./Galaxy";
import { useLanguage } from "./LanguageProvider";

export const ContactUs = ({
  bgColor = "var(--premium-bg)",
  mainColor = "#ae0001",
}) => {
  const { currentLang } = useLanguage();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const isAR = currentLang === "ar";
  const isDark = bgColor === "var(--premium-bg)" || bgColor === "#0f1218";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  const translations = {
    EN: {
      eyebrow: "Support",
      title: ["Contact", "Us"],
      subtitle: "Get in touch with us for any questions or inquiries",
      location: "Location",
      locationDetails: "Rock Plaza - Fifth Settlement",
      phone: "Phone",
      email: "Email",
      formTitle: "Send a Message",
      nameLabel: "Full Name *",
      emailLabel: "Email *",
      phoneLabel: "Phone *",
      messageLabel: "Message *",
      send: "Send Message",
      sending: "Sending...",
      success: "Thank you! We'll contact you soon.",
    },
    AR: {
      eyebrow: "الدعم",
      title: ["اتصل", "بنا"],
      subtitle: "تواصل معنا لأي استفسارات أو استعلامات",
      location: "الموقع",
      locationDetails: "التجمع الخامس - روك بلازا",
      phone: "الهاتف",
      email: "البريد الإلكتروني",
      formTitle: "أرسل رسالة",
      nameLabel: "الاسم الكامل *",
      emailLabel: "البريد الإلكتروني *",
      phoneLabel: "رقم الهاتف *",
      messageLabel: "الرسالة *",
      send: "أرسل الرسالة",
      sending: "جاري الإرسال...",
      success: "شكراً لك! سنتواصل معك قريباً.",
    },
  };

  const t = (key) =>
    translations[isAR ? "AR" : "EN"]?.[key] || translations.EN[key] || key;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await dispatch(
        Add_Client_Action({
          ...formData,
          message: "Contact Form: " + formData.message,
        }),
      );
      setIsSubmitted(true);
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialMedia = [
    {
      icon: <FaFacebook />,
      link: "https://www.facebook.com/profile.php?id=61588256249191",
    },
    {
      icon: <FaInstagram />,
      link: "https://www.instagram.com/flyvia.services/",
    },
    { icon: <FaYoutube />, link: "https://www.youtube.com/@Flyvia-x8d/videos" },
    { icon: <FaTiktok />, link: "https://www.tiktok.com/@flyvia.services" },
    { icon: <FaWhatsapp />, link: "https://wa.me/34614909102" },
  ];

  return (
    <section
      ref={sectionRef}
      id="contact"
      className="contact-section relative overflow-hidden"
      style={{
        background: bgColor,
        marginTop: "-120px",
        paddingTop: "100px",
        paddingBottom: "100px",
        clipPath: "polygon(0% 0%, 100% 4%, 100% 100%, 0% 100%)",
        direction: isAR ? "rtl" : "ltr",
      }}
    >
      <style>{`
        .contact-section { font-family: var(--font-primary); }
        .c-eyebrow { display: block; margin-bottom: 24px; color: ${mainColor}; font-size: 13px; font-weight: 600; letter-spacing: 0.4em; text-transform: uppercase; font-family: var(--font-secondary); }
        .c-title { font-family: var(--font-heading); font-weight: 300; line-height: 1.1; letter-spacing: -0.01em; color: ${isDark ? "#f5f0e8" : "#1a1a1a"}; opacity: 0; transform: translateY(30px); transition: all 1s ease; }
        .c-title.visible { opacity: 1; transform: translateY(0); }
        .c-title em { font-style: italic; color: ${mainColor}; font-family: 'Cormorant Garamond', serif; }
        [dir="rtl"] .c-title em { font-family: inherit; }
        
        .c-subtitle { max-width: 480px; margin: 32px 0 60px; color: ${isDark ? "rgba(255,255,255,0.4)" : "#666"}; font-size: 14px; font-weight: 300; line-height: 1.8; font-family: var(--font-secondary); opacity: 0; transform: translateY(30px); transition: all 1s ease 150ms; }
        .c-subtitle.visible { opacity: 1; transform: translateY(0); }
        .contact-info-card { background: ${isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"}; border: 1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)"}; padding: 40px; border-radius: 32px; }
        .info-item { display: flex; align-items: flex-start; gap: 20px; margin-bottom: 32px; }
        .info-icon { width: 44px; height: 44px; border-radius: 50%; background: ${mainColor}11; color: ${mainColor}; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
        .info-text h4 { font-family: var(--font-secondary); font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: ${mainColor}; margin-bottom: 6px; }
        .info-text p { font-family: var(--font-primary); color: ${isDark ? "#f5f0e8" : "#1a1a1a"}; font-size: 14px; margin: 0; font-weight: 300; }
        .contact-form-card { background: ${isDark ? "rgba(255,255,255,0.04)" : "#fff"}; border: 1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)"}; padding: 50px; border-radius: 32px; box-shadow: 0 40px 80px rgba(0,0,0,0.2); }
        .form-control { font-family: var(--font-primary); background: transparent !important; border: none !important; border-bottom: 1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)"} !important; border-radius: 0 !important; padding: 12px 0 !important; color: ${isDark ? "#fff" : "#000"} !important; font-size: 14px !important; }
        .form-control:focus { border-bottom-color: ${mainColor} !important; box-shadow: none !important; }
        .form-label { font-family: var(--font-secondary); font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: ${isDark ? "rgba(255,255,255,0.3)" : "#888"}; margin-bottom: 0; }
        .submit-btn { background: ${mainColor} !important; border: none !important; border-radius: 100px !important; padding: 16px 40px !important; font-weight: 600 !important; letter-spacing: 0.2em !important; text-transform: uppercase !important; font-family: var(--font-secondary) !important; margin-top: 32px; font-size: 11px !important; transition: all 0.4s !important; }
        .submit-btn:hover { transform: translateY(-4px); box-shadow: 0 20px 40px ${mainColor}44; }
        .social-links { display: flex; gap: 12px; margin-top: 40px; }
        .social-link { width: 40px; height: 40px; border-radius: 50%; border: 1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}; color: ${isDark ? "rgba(255,255,255,0.5)" : "#666"}; display: flex; align-items: center; justify-content: center; transition: all 0.3s; text-decoration: none; }
        .social-link:hover { background: ${mainColor}; color: #fff; border-color: ${mainColor}; transform: translateY(-3px); }
        .c-bg-text { position: absolute; bottom: 0; right: -5%; font-family: var(--font-heading); font-size: 20vw; font-weight: 900; color: rgba(255,255,255,0.015); pointer-events: none; line-height: 1; z-index: 0; }
      `}</style>
      <div className="c-bg-text">CONTACT</div>
      <Container className="max-w-screen-xl mx-auto px-8 relative z-10">
        <Row className="gy-2">
          <Col lg={5} className="">
            <span className="c-eyebrow">{t("eyebrow")}</span>
            <h2
              className={`c-title !p-0 !m-0  ${isVisible ? "visible" : ""}`}
              style={{ fontSize: "clamp(40px, 5vw, 64px)" }}
            >
              {t("title")[0]} <em>{t("title")[1]}</em>
            </h2>
            <p className={`c-subtitle !text-lg  ${isVisible ? "visible" : ""}`}>
              {t("subtitle")}
            </p>
            <div className="contact-info-card">
              <div className="info-item">
                <div className="info-icon">
                  <FaMapMarkerAlt />
                </div>
                <div className="info-text">
                  <h4>{t("location")}</h4>
                  <p>{t("locationDetails")}</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">
                  <FaPhone />
                </div>
                <div className="info-text">
                  <h4>{t("phone")}</h4>
                  <p>+34 614 909 102</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">
                  <FaEnvelope />
                </div>
                <div className="info-text">
                  <h4>{t("email")}</h4>
                  <p>info@flyvia.com</p>
                </div>
              </div>
              <div className="social-links">
                {socialMedia.map((s, i) => (
                  <a
                    key={i}
                    href={s.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
          </Col>
          <Col lg={7}>
            <div className="contact-form-card">
              <h3
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "28px",
                  fontWeight: 300,
                  marginBottom: "32px",
                  color: isDark ? "#f5f0e8" : "#1a1a1a",
                }}
              >
                {t("formTitle")}
              </h3>
              <Form onSubmit={handleSubmit}>
                <Row className="gy-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="form-label">
                        {t("nameLabel")}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="form-label">
                        {t("emailLabel")}
                      </Form.Label>
                      <Form.Control
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="form-label">
                        {t("phoneLabel")}
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="form-label">
                        {t("messageLabel")}
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.message}
                        onChange={(e) =>
                          setFormData({ ...formData, message: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Button
                  type="submit"
                  className="submit-btn !flex !items-center !justify-center"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("sending") : t("send")}{" "}
                  <FaPaperPlane
                    style={{ marginInlineStart: "10px", fontSize: "10px" }}
                  />
                </Button>
                {isSubmitted && (
                  <p
                    className="mt-4"
                    style={{
                      color: "#4caf50",
                      fontFamily: "var(--font-secondary)",
                      fontSize: "13px",
                    }}
                  >
                    {t("success")}
                  </p>
                )}
              </Form>
            </div>
          </Col>
        </Row>
      </Container>

      <div
        style={{
          backgroundImage: "url('/145.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.4,
          backgroundAttachment: "fixed",
        }}
        className="absolute inset-0 w-full h-full pointer-events-none"
      ></div>
      <Galaxy />
    </section>
  );
};
